import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { GuaraniFormatPipe } from '../../../../pipes/guarani-format.pipe';
import { RecebimentoMensalDTO } from '../../../../models/master/master-dashboard.model';

/**
 * Recebimentos por mes da Area Master — serie temporal em BARRAS.
 *
 * Nunca pizza, rosca, barra empilhada ou treemap: todo grafico de composicao AFIRMA que as
 * partes somam o todo, e nesta tela nenhum conjunto de campos soma.
 *
 * O componente nao faz HTTP: recebe a serie ja consultada pela pagina.
 * O rotulo do eixo X vem pronto do backend (`rotulo`) e NAO e montado nem localizado aqui.
 */
@Component({
  selector: 'app-master-dashboard-recebimentos-chart',
  templateUrl: './master-dashboard-recebimentos-chart.component.html',
  styleUrls: ['./master-dashboard-recebimentos-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GuaraniFormatPipe],
})
export class MasterDashboardRecebimentosChartComponent implements OnInit, OnChanges, OnDestroy {

  @Input() dados: RecebimentoMensalDTO[] = [];
  @Input() loading = false;

  chartData: any;
  chartOptions: any;
  ariaLabel = '';

  texts = {
    titulo: '',
    sem_dados: '',
    pagamentos: '',
  };

  private langChangeSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private guaraniFormat: GuaraniFormatPipe,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.buildChart();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
      this.buildChart();
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dados']) {
      this.buildChart();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  /** Serie de um mes so nao e grafico: vira frase. Zero meses tambem nao e erro. */
  get temGrafico(): boolean {
    return (this.dados?.length ?? 0) >= 2;
  }

  get temSerieCurta(): boolean {
    return (this.dados?.length ?? 0) === 1;
  }

  get estaVazio(): boolean {
    return (this.dados?.length ?? 0) === 0;
  }

  /** Texto da serie de um mes so, ja formatado — evita pipe encadeado no template. */
  get textoSerieCurta(): string {
    const item = this.dados?.[0];
    if (!item) return '';
    return this.translate.instant('master.planos.dashboard.recebimentos.serie_curta', {
      rotulo: item.rotulo,
      valor: this.guaraniFormat.transform(item.valor),
      quantidade: item.quantidade,
    });
  }

  formatarValor(valor: number): string {
    return this.guaraniFormat.transform(valor);
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('master.planos.dashboard.recebimentos.titulo'),
      sem_dados: this.translate.instant('master.planos.dashboard.recebimentos.sem_dados'),
      pagamentos: this.translate.instant('master.planos.dashboard.recebimentos.tooltip_pagamentos'),
    };
    this.ariaLabel = this.texts.titulo;
  }

  private buildChart(): void {
    const serie = this.dados ?? [];

    this.chartData = {
      labels: serie.map((item) => item.rotulo),
      datasets: [
        {
          label: this.texts.titulo,
          data: serie.map((item) => item.valor),
          backgroundColor: '#165c7d',
          hoverBackgroundColor: '#1d7aa6',
          borderRadius: 4,
          maxBarThickness: 48,
        },
      ],
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            // Valor em Guarani pelo MESMO pipe da tela. Sem `footer` de total: somar meses
            // aqui seria inventar um numero que o contrato nao oferece.
            label: (ctx: any) => {
              const indice = ctx.dataIndex ?? 0;
              const item = serie[indice];
              const valor = this.guaraniFormat.transform(ctx.parsed?.y ?? 0);
              const quantidade = item?.quantidade ?? 0;
              return `${valor} · ${quantidade} ${this.texts.pagamentos}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
        },
        y: {
          min: 0,
          ticks: {
            maxTicksLimit: 6,
            callback: (valor: number) => this.formatarEixoY(valor),
          },
        },
      },
    };
  }

  /** Eixo Y compacto: `Gs. 1,2 M`. Sem isso o rotulo em Guarani corta o grafico. */
  private formatarEixoY(valor: number): string {
    const absoluto = Math.abs(valor);
    if (absoluto >= 1_000_000_000) {
      return `Gs. ${this.comUmaCasa(valor / 1_000_000_000)} B`;
    }
    if (absoluto >= 1_000_000) {
      return `Gs. ${this.comUmaCasa(valor / 1_000_000)} M`;
    }
    if (absoluto >= 1_000) {
      return `Gs. ${this.comUmaCasa(valor / 1_000)} K`;
    }
    return this.guaraniFormat.transform(valor);
  }

  private comUmaCasa(valor: number): string {
    return valor.toLocaleString('es-PY', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }
}
